import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, ArrowLeft, Loader2, Check } from "lucide-react";
import axios from "axios";
import { useLanguage, LangSwitcher } from "@/context/LanguageContext";
import { toast } from "sonner";
import { tr } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BG = "https://images.unsplash.com/photo-1776218643389-98159ee68366?q=80&w=1920&auto=format&fit=crop";

export default function Register() {
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", organization: "" });
    const [busy, setBusy] = useState(false);
    useLanguage();

    const submit = async (e) => {
        e?.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            await axios.post(`${API}/auth/register`, { ...form, email: form.email.trim().toLowerCase(), role: "developer" });
            toast.success(tr("Account created — sign in to your applicant portal"));
            navigate("/developer/login");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Registration failed");
        } finally {
            setBusy(false);
        }
    };

    const inputCls = "w-full rounded-[10px] bg-white/10 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent transition-all";

    return (
        <div className="min-h-screen relative flex items-center justify-center overflow-hidden" data-testid="register-page">
            <div className="absolute inset-0 bg-cover bg-center scale-105" style={{ backgroundImage: `url(${BG})`, filter: "blur(6px) brightness(0.55)" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(10,37,64,0.8) 0%, rgba(10,37,64,0.45) 55%, rgba(184,134,11,0.25) 100%)" }} />

            <Link to="/developer/login" data-testid="register-back" className="absolute top-6 left-6 z-10 inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors">
                <ArrowLeft className="w-4 h-4" /> {tr("Back to sign in")}
            </Link>
            <div className="absolute top-6 right-6 z-10"><LangSwitcher dark testid="register-lang-switcher" /></div>

            <motion.div initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}
                className="relative z-10 w-full max-w-md mx-4">
                <div className="rounded-[20px] p-8 sm:p-10 shadow-2xl border border-white/15" style={{ background: "rgba(13,33,54,0.72)", backdropFilter: "blur(28px)" }}>
                    <div className="flex flex-col items-center text-center">
                        <img src="/assets/logo-light.png" alt="LandLogic NexusOne" className="w-20 h-auto drop-shadow-2xl" />
                        <div className="mt-3 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#00B4D8]/15 border border-[#00B4D8]/30 text-[11px] font-bold text-[#7CD4EA]">
                            <Building2 className="w-3.5 h-3.5" /> {tr("DEVELOPER REGISTRATION")}
                        </div>
                        <p className="text-sm text-white/60 mt-3">{tr("Create your applicant account to submit and track files")}</p>
                    </div>

                    <form onSubmit={submit} className="mt-7 space-y-3.5">
                        <input data-testid="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={tr("Full name")} className={inputCls} />
                        <input data-testid="register-organization" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder={tr("Company / organization")} className={inputCls} />
                        <input data-testid="register-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={tr("Email address")} autoComplete="email" className={inputCls} />
                        <input data-testid="register-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={tr("Password (min 8 characters)")} autoComplete="new-password" className={inputCls} />
                        <button type="submit" data-testid="register-submit" disabled={busy || !form.name || !form.email || form.password.length < 8}
                            className="w-full py-3.5 rounded-[10px] bg-[#00B4D8] hover:bg-[#00c3eb] text-white font-bold text-sm transition-all hover:shadow-lg hover:shadow-[#00B4D8]/30 active:scale-[0.98] disabled:opacity-40 inline-flex items-center justify-center gap-2">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            {tr("Create applicant account")}
                        </button>
                    </form>

                    <div className="mt-6 text-center text-xs text-white/50">
                        {tr("Already registered?")} <Link to="/developer/login" data-testid="register-signin-link" className="text-[#7CD4EA] font-bold hover:underline">{tr("Sign in")}</Link>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
